"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import CommonTabs from "@/components/utils/select/tabs/CommonTabs";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";
import { mainnet } from "viem/chains";
import NowMintingCountdown from "@/components/home/now-minting/NowMintingCountdown";
import { publicEnv } from "@/config/env";
import { MEMES_CONTRACT } from "@/constants/constants";
import { useTitle } from "@/contexts/TitleContext";
import type { DBResponse } from "@/entities/IDBResponse";
import type { MemesExtendedData, NFT, NftRank, NftTDH } from "@/entities/INFT";
import type { ConsolidatedTDH } from "@/entities/ITDH";
import type { Transaction } from "@/entities/ITransaction";
import { areEqualAddresses } from "@/helpers/Helpers";
import { fetchUrl } from "@/services/6529api";
import { commonApiFetch } from "@/services/api/common-api";
import NftNavigation from "../nft-navigation/NftNavigation";
import MemeCalendarPeriods from "./MemeCalendarPeriods";
import { MemePageArtViewer } from "./MemePageArtViewer";
import { MemePageCollectorsSubMenu } from "./MemePageCollectors";
import { MemePageLiveRightMenu, MemePageLiveSubMenu } from "./MemePageLive";
import { MemePageReferencesSubMenu } from "./MemePageReferences";
import { MemePageNavigationSkeleton, MemePageSkeleton, MemePageTitleSkeleton } from "./MemePageSkeleton";
import { MemePageYourCardsRightMenu, MemePageYourCardsSubMenu } from "./MemePageYourCards";
import { getMemeTabTitle, MEME_FOCUS, MEME_TABS } from "./MemeShared";
import styles from "./TheMemes.module.scss";
import UpcomingMemePage from "./UpcomingMemePage";
const MemePageActivity = dynamic(() => import("./MemePageActivity").then(mod => mod.MemePageActivity));
const MemePageTimeline = dynamic(() => import("./MemePageTimeline").then(mod => mod.MemePageTimeline));
const ACTIVITY_PAGE_SIZE = 25;
const VISIBLE_MEME_TABS = [MEME_FOCUS.LIVE, MEME_FOCUS.COLLECTORS, MEME_FOCUS.HISTORY, MEME_FOCUS.REFERENCES].map(focus => MEME_TABS.find(tab => tab.focus === focus)).filter((tab): tab is (typeof MEME_TABS)[number] => tab !== undefined);
enum MEME_HISTORY_TAB {
  ACTIVITY = "activity",
  TIMELINE = "timeline",
  YOUR_TRANSACTIONS = "your-transactions",
}
const MEME_HISTORY_TABS: {
  readonly focus: MEME_HISTORY_TAB;
  readonly title: string;
}[] = [{
  focus: MEME_HISTORY_TAB.ACTIVITY,
  title: "Card Activity"
}, {
  focus: MEME_HISTORY_TAB.YOUR_TRANSACTIONS,
  title: "Your Transactions"
}, {
  focus: MEME_HISTORY_TAB.TIMELINE,
  title: "Timeline"
}];
const MEME_FOCUS_VALUES: readonly string[] = Object.values(MEME_FOCUS);
const MEME_TAB_BUTTON_BASE_CLASS_NAME = "tw-m-0 tw-flex tw-items-center tw-whitespace-nowrap tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-bg-transparent tw-px-1 tw-py-4 tw-text-base tw-font-semibold tw-leading-4 tw-no-underline tw-transition tw-duration-300 tw-ease-out";
function getMemePageTabButtonClassName(isActive: boolean) {
  return `${MEME_TAB_BUTTON_BASE_CLASS_NAME} ${isActive ? "tw-pointer-events-none tw-border-primary-400 tw-text-iron-100" : "tw-cursor-pointer tw-border-transparent tw-text-iron-500 hover:tw-border-gray-300 hover:tw-text-iron-100"}`;
}
function MemePageTabButton(t0) {
  const $ = _c(6);
  const {
    title,
    isActive,
    onClick
  } = t0;
  let t1;
  if ($[0] !== isActive) {
    t1 = getMemePageTabButtonClassName(isActive);
    $[0] = isActive;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onClick || $[3] !== t1 || $[4] !== title) {
    t2 = <button type="button" className={t1} onClick={onClick}>{title}</button>;
    $[2] = onClick;
    $[3] = t1;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function parseMemeFocus(focus: string | null): MEME_FOCUS | undefined {
  if (focus === null || !MEME_FOCUS_VALUES.includes(focus)) {
    return undefined;
  }
  return focus as MEME_FOCUS;
}
function getHistoryTabForFocus(focus: MEME_FOCUS | undefined): MEME_HISTORY_TAB {
  if (focus === MEME_FOCUS.TIMELINE) {
    return MEME_HISTORY_TAB.TIMELINE;
  }
  if (focus === MEME_FOCUS.YOUR_TRANSACTIONS) {
    return MEME_HISTORY_TAB.YOUR_TRANSACTIONS;
  }
  if (focus === MEME_FOCUS.YOUR_CARDS) {
    return MEME_HISTORY_TAB.YOUR_TRANSACTIONS;
  }
  return MEME_HISTORY_TAB.ACTIVITY;
}
function getRouteFocus(activeTab: MEME_FOCUS, activeHistoryTab: MEME_HISTORY_TAB) {
  if (activeTab !== MEME_FOCUS.HISTORY) {
    return activeTab;
  }
  if (activeHistoryTab === MEME_HISTORY_TAB.TIMELINE) {
    return MEME_FOCUS.TIMELINE;
  }
  if (activeHistoryTab === MEME_HISTORY_TAB.YOUR_TRANSACTIONS) {
    return MEME_FOCUS.YOUR_TRANSACTIONS;
  }
  return MEME_FOCUS.ACTIVITY;
}
export default function MemePage(t0) {
  const $ = _c(74);
  const {
    nftId
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    setTitle
  } = useTitle();
  const {
    connectedProfile
  } = useContext(AuthContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [connectedWallets, setConnectedWallets] = useState(t1);
  let t2;
  if ($[1] !== searchParams) {
    t2 = parseMemeFocus(searchParams.get("focus"));
    $[1] = searchParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const focusParam = t2;
  let t3;
  bb0: {
    if (focusParam === undefined) {
      t3 = undefined;
      break bb0;
    }
    if (focusParam === MEME_FOCUS.THE_ART) {
      t3 = MEME_FOCUS.LIVE;
      break bb0;
    }
    if (focusParam === MEME_FOCUS.YOUR_CARDS || focusParam === MEME_FOCUS.ACTIVITY || focusParam === MEME_FOCUS.TIMELINE || focusParam === MEME_FOCUS.YOUR_TRANSACTIONS) {
      t3 = MEME_FOCUS.HISTORY;
      break bb0;
    }
    t3 = focusParam;
  }
  const resolvedRouterFocus = t3;
  const activeTab = resolvedRouterFocus ?? MEME_FOCUS.LIVE;
  const requestedHistoryTab = activeTab === MEME_FOCUS.HISTORY ? getHistoryTabForFocus(focusParam) : MEME_HISTORY_TAB.ACTIVITY;
  let t4;
  if ($[3] !== connectedProfile?.wallets) {
    t4 = () => {
      setConnectedWallets(connectedProfile?.wallets?.map(_temp) ?? []);
    };
    $[3] = connectedProfile?.wallets;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== connectedProfile) {
    t5 = [connectedProfile];
    $[5] = connectedProfile;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== searchParams) {
    t6 = searchParams.toString();
    $[7] = searchParams;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const searchParamsString = t6;
  const [nft, setNft] = useState();
  const [nftNotFound, setNftNotFound] = useState(false);
  const [nftMeta, setNftMeta] = useState();
  const [nftBalance, setNftBalance] = useState(0);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const [transactions, setTransactions] = useState(t7);
  const [myOwner, setMyOwner] = useState();
  const [myTDH, setMyTDH] = useState();
  const [myRank, setMyRank] = useState();
  const [userLoaded, setUserLoaded] = useState(false);
  const hasUserTransactions = userLoaded && connectedWallets.length > 0 && transactions.length > 0;
  const activeHistoryTab = requestedHistoryTab === MEME_HISTORY_TAB.YOUR_TRANSACTIONS && !hasUserTransactions ? MEME_HISTORY_TAB.ACTIVITY : requestedHistoryTab;
  let t8;
  if ($[10] !== hasUserTransactions) {
    t8 = MEME_HISTORY_TABS.filter(tab => tab.focus !== MEME_HISTORY_TAB.YOUR_TRANSACTIONS || hasUserTransactions);
    $[10] = hasUserTransactions;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const visibleHistoryTabs = t8;
  let t9;
  if ($[12] !== visibleHistoryTabs) {
    t9 = visibleHistoryTabs.map(_temp2);
    $[12] = visibleHistoryTabs;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const visibleHistoryTabItems = t9;
  const routeFocus = getRouteFocus(activeTab, activeHistoryTab);
  useEffect(() => {
    setTitle(getMemeTabTitle("The Memes", nftId, nft, routeFocus));
  }, [nft, nftId, routeFocus, setTitle]);
  let t10;
  if ($[14] !== pathname || $[15] !== router || $[16] !== searchParamsString) {
    t10 = function replaceRouteFocus(nextFocus) {
      const params = new URLSearchParams(searchParamsString);
      params.set("focus", nextFocus);
      const queryString = params.toString();
      router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
        scroll: false
      });
    };
    $[14] = pathname;
    $[15] = router;
    $[16] = searchParamsString;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const replaceRouteFocus = t10;
  const setActiveMemeTab = function setActiveMemeTab(nextTab) {
    if (nextTab === MEME_FOCUS.HISTORY) {
      replaceRouteFocus(getRouteFocus(MEME_FOCUS.HISTORY, activeHistoryTab));
      return;
    }
    replaceRouteFocus(nextTab);
  };
  let t11;
  if ($[18] !== replaceRouteFocus) {
    t11 = function setActiveHistoryMemeTab(nextTab_0) {
      replaceRouteFocus(getRouteFocus(MEME_FOCUS.HISTORY, nextTab_0));
    };
    $[18] = replaceRouteFocus;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const setActiveHistoryMemeTab = t11;
  let t12;
  let t13;
  if ($[20] !== nftId) {
    t12 = () => {
      if (!nftId) {
        return;
      }
      let cancelled = false;
      const metaPromise = fetchUrl(`${publicEnv.API_ENDPOINT}/api/memes_extended_data?id=${nftId}`);
      const nftPromise = fetchUrl(`${publicEnv.API_ENDPOINT}/api/nfts?id=${nftId}&contract=${MEMES_CONTRACT}`);
      Promise.all([metaPromise, nftPromise]).then(t14 => {
        const [metaResponse, nftResponse] = t14;
        if (cancelled) {
          return;
        }
        const nftMetas = metaResponse.data;
        if (Array.isArray(nftMetas) && nftMetas.length === 1) {
          setNftMeta(nftMetas[0]);
          const mynft = nftResponse.data?.[0];
          setNft(mynft);
          setNftNotFound(false);
        } else {
          setNftMeta(undefined);
          setNft(undefined);
          setNftNotFound(true);
        }
      }).catch(() => {
        if (!cancelled) {
          setNftMeta(undefined);
          setNft(undefined);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t13 = [nftId];
    $[20] = nftId;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  let t14;
  if ($[23] !== connectedWallets) {
    t14 = function updateNftBalances(data) {
      let countIn = 0;
      let countOut = 0;
      data.map(d => {
        if (connectedWallets.some(w_0 => areEqualAddresses(w_0, d.from_address))) {
          countOut = countOut + d.token_count;
          countOut;
        }
        if (connectedWallets.some(w_1 => areEqualAddresses(w_1, d.to_address))) {
          countIn = countIn + d.token_count;
          countIn;
        }
      });
      setNftBalance(countIn - countOut);
    };
    $[23] = connectedWallets;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const updateNftBalances = t14;
  let t15;
  if ($[25] !== connectedWallets || $[26] !== nftId || $[27] !== updateNftBalances) {
    t15 = () => {
      if (connectedWallets.length && nftId) {
        fetchUrl(`${publicEnv.API_ENDPOINT}/api/transactions?contract=${MEMES_CONTRACT}&wallet=${connectedWallets.join(",")}&id=${nftId}`).then(response => {
          setTransactions(response.data);
          updateNftBalances(response.data);
          setUserLoaded(true);
        });
      } else {
        setNftBalance(0);
        setUserLoaded(true);
        setTransactions([]);
      }
    };
    $[25] = connectedWallets;
    $[26] = nftId;
    $[27] = updateNftBalances;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== connectedWallets || $[30] !== nftId) {
    t16 = [nftId, connectedWallets];
    $[29] = connectedWallets;
    $[30] = nftId;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  useEffect(t15, t16);
  let t17;
  if ($[32] !== connectedProfile || $[33] !== connectedWallets.length || $[34] !== nftId) {
    t17 = () => {
      if (connectedWallets.length > 0 && nftId && connectedProfile?.consolidation_key) {
        commonApiFetch({
          endpoint: `tdh/consolidation/${connectedProfile.consolidation_key}`
        }).then(response_0 => {
          setMyOwner(response_0);
          setMyTDH(response_0.memes.find(m => m.id === Number.parseInt(nftId, 10)));
          setMyRank(response_0.memes_ranks.find(m_0 => m_0.id === Number.parseInt(nftId, 10)));
        }).catch(() => {
          setMyOwner(undefined);
          setMyTDH(undefined);
          setMyRank(undefined);
        });
      }
    };
    $[32] = connectedProfile;
    $[33] = connectedWallets.length;
    $[34] = nftId;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = connectedProfile?.consolidation_key;
  let t19;
  if ($[36] !== connectedWallets || $[37] !== nftId || $[38] !== t18) {
    t19 = [nftId, connectedWallets, t18];
    $[36] = connectedWallets;
    $[37] = nftId;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  useEffect(t17, t19);
  let t20;
  if ($[40] !== connectedWallets || $[41] !== myOwner || $[42] !== myRank || $[43] !== myTDH || $[44] !== nft || $[45] !== nftBalance || $[46] !== nftMeta || $[47] !== transactions || $[48] !== userLoaded) {
    t20 = function printStaticCardHeader() {
      if (!nft) {
        return null;
      }
      const hasMintingBox = isLastCard;
      const cardHeaderClassName = hasMintingBox ? "tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-y-0 lg:tw-grid-cols-[minmax(0,11fr)_minmax(0,9fr)] lg:tw-items-center lg:tw-gap-x-16" : "tw-mb-6 tw-grid tw-grid-cols-1 tw-gap-x-10 lg:tw-grid-cols-[minmax(0,11fr)_minmax(0,9fr)] xl:tw-gap-x-16";
      const artworkColumnClassName = hasMintingBox ? "tw-relative lg:tw-flex lg:tw-flex-col lg:tw-self-stretch tw-order-2 tw-mt-6 tw-self-start lg:tw-order-none lg:tw-col-start-1 lg:tw-row-start-1 lg:tw-mt-0" : "tw-relative lg:tw-flex lg:tw-flex-col lg:tw-self-stretch";
      const detailsColumnClassName = hasMintingBox ? "tw-contents [&>*:first-child]:tw-order-1 [&>*:nth-child(2)]:tw-order-3 [&>*:nth-child(2)]:tw-pt-4 lg:tw-col-start-2 lg:tw-row-start-1 lg:tw-block lg:[&>*]:tw-order-none lg:[&>*]:tw-w-full lg:[&>*:nth-child(2)]:tw-pt-8" : undefined;
      return <div className={cardHeaderClassName}><div className={artworkColumnClassName}><div className={`${styles.nftImageWrapper ?? ""} lg:tw-flex-1`}><MemePageArtViewer key={`${nft.contract}-${nft.id}`} nft={nft} showBalance={true} /></div>{userLoaded && <MemePageYourCardsRightMenu show={true} transactions={transactions} wallets={connectedWallets} nft={nft} nftBalance={nftBalance} myOwner={myOwner} myTDH={myTDH} myRank={myRank} />}</div><div className={detailsColumnClassName}>{isLastCard && <NowMintingCountdown nftId={nft.id} contract={MEMES_CONTRACT} chainId={mainnet.id} fullWidth={true} />}<MemePageLiveRightMenu show={true} nft={nft} nftMeta={nftMeta} /></div></div>;
    };
    $[40] = connectedWallets;
    $[41] = myOwner;
    $[42] = myRank;
    $[43] = myTDH;
    $[44] = nft;
    $[45] = nftBalance;
    $[46] = nftMeta;
    $[47] = transactions;
    $[48] = userLoaded;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const printStaticCardHeader = t20;
  const printTabs = function printTabs() {
    if (!nft) {
      return null;
    }
    return <nav aria-label="Meme page sections" className="tw-relative tw-mb-8 tw-overflow-hidden tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800"><div className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden"><div className="-tw-mb-px tw-flex tw-min-w-max tw-gap-x-3 lg:tw-gap-x-4">{VISIBLE_MEME_TABS.map(tab_1 => <MemePageTabButton key={`${nft.id}-${tab_1.focus}-tab`} title={tab_1.title} isActive={activeTab === tab_1.focus} onClick={() => setActiveMemeTab(tab_1.focus)} />)}</div></div></nav>;
  };
  const printHistoryTabs = function printHistoryTabs() {
    if (!nft || activeTab !== MEME_FOCUS.HISTORY) {
      return null;
    }
    return <nav aria-label="Meme history sections" className="tw-pb-8"><div className="tw-w-fit tw-max-w-full"><CommonTabs items={visibleHistoryTabItems} activeItem={activeHistoryTab} filterLabel="Meme history sections" setSelected={setActiveHistoryMemeTab} fill={false} /></div></nav>;
  };
  const printContent = function printContent() {
    return <><div className="tw-p-0"><MemePageLiveSubMenu show={activeTab === MEME_FOCUS.LIVE} nft={nft} nftMeta={nftMeta} nftBalance={nftBalance} defaultAdditionalDetailsOpen={focusParam === MEME_FOCUS.THE_ART} /><MemePageReferencesSubMenu show={activeTab === MEME_FOCUS.REFERENCES} nft={nft} />{userLoaded && <MemePageYourCardsSubMenu show={activeTab === MEME_FOCUS.HISTORY && activeHistoryTab === MEME_HISTORY_TAB.YOUR_TRANSACTIONS} transactions={transactions} />}<MemePageCollectorsSubMenu show={activeTab === MEME_FOCUS.COLLECTORS} nft={nft} nftMeta={nftMeta} /></div>{activeTab === MEME_FOCUS.HISTORY && activeHistoryTab === MEME_HISTORY_TAB.ACTIVITY && <MemePageActivity show={true} nft={nft} pageSize={ACTIVITY_PAGE_SIZE} />}{activeTab === MEME_FOCUS.HISTORY && activeHistoryTab === MEME_HISTORY_TAB.TIMELINE && <MemePageTimeline show={true} nft={nft} />}</>;
  };
  const isLastCard = nftMeta?.collection_size === nft?.id;
  const isLoadingNft = !nft && !nftNotFound;
  const t21 = "tailwind-scope tw-min-h-[calc(100vh-100px)] tw-border tw-border-y-0 tw-border-l-0 tw-border-solid tw-border-iron-800 tw-bg-[#0D0D0F] tw-pb-5 tw-text-white";
  const t22 = "tw-px-4 tw-py-4 md:tw-px-6 md:tw-pb-10 lg:tw-px-8";
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="tw-mb-0 tw-flex tw-items-center"><Link href="/the-memes" className="tw-group -tw-ml-2 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-2 tw-py-2 tw-text-xs tw-font-semibold tw-leading-5 tw-text-iron-300 tw-no-underline tw-transition-colors hover:tw-text-iron-400 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400"><ArrowLeftIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-transition-transform group-hover:-tw-translate-x-0.5" />The Memes</Link></div>;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== nft || $[52] !== nftMeta) {
    t24 = nftMeta && nft && <div className="tw-ml-auto tw-flex tw-min-w-0 tw-items-center md:tw-ml-0"><MemeCalendarPeriods id={nft.id} seasonHref={`/the-memes?szn=${nftMeta.season}&sort=age&sort_dir=ASC`} showOnlySeasonOnMobile={true} /></div>;
    $[51] = nft;
    $[52] = nftMeta;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-2 md:tw-justify-start">{t23}{t24}</div>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== isLoadingNft || $[57] !== nft || $[58] !== nftMeta || $[59] !== searchParams) {
    t26 = nftMeta && nft ? <div className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-3 md:tw-flex-wrap md:tw-justify-start"><div className="tw-order-2 tw-flex tw-shrink-0 tw-justify-end md:tw-order-1"><NftNavigation nftId={nft.id} path="/the-memes" startIndex={1} endIndex={nftMeta.collection_size} params={searchParams} /></div><div className="tw-order-1 tw-min-w-0 tw-flex-1 md:tw-order-2"><h1 className="tw-mb-0 tw-flex tw-min-w-0 tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1 md:tw-flex-nowrap md:tw-gap-x-0" aria-label={`Card ${nft.id} — ${nft.name}`}><span className="tw-mb-0 tw-shrink-0 tw-text-lg tw-font-normal tw-leading-tight tw-text-iron-400 sm:tw-text-2xl">Card {nft.id}</span><span aria-hidden="true" className="tw-mx-3 tw-h-5 tw-w-px tw-self-center tw-bg-white/[0.16] sm:tw-h-6" /><span className="tw-mb-0 tw-min-w-0 tw-whitespace-normal tw-break-words tw-text-lg tw-font-semibold tw-leading-tight tw-text-iron-100 sm:tw-text-2xl md:tw-truncate">{nft.name}</span></h1></div></div> : isLoadingNft && <div className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-3 md:tw-flex-wrap md:tw-justify-start"><div className="tw-order-2 tw-flex tw-shrink-0 tw-justify-end md:tw-order-1"><MemePageNavigationSkeleton /></div><div className="tw-order-1 tw-min-w-0 tw-flex-1 md:tw-order-2"><MemePageTitleSkeleton /></div></div>;
    $[56] = isLoadingNft;
    $[57] = nft;
    $[58] = nftMeta;
    $[59] = searchParams;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t25 || $[62] !== t26) {
    t27 = <header className="tw-pb-8"><div className="tw-flex tw-flex-col tw-gap-4">{t25}{t26}</div></header>;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== isLoadingNft) {
    t28 = isLoadingNft && <MemePageSkeleton />;
    $[64] = isLoadingNft;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  const t29 = nftMeta && nft && <>{printStaticCardHeader()}{printTabs()}{printHistoryTabs()}{printContent()}</>;
  let t30;
  if ($[66] !== nftId || $[67] !== nftNotFound) {
    t30 = nftNotFound && <UpcomingMemePage id={nftId} />;
    $[66] = nftId;
    $[67] = nftNotFound;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t27 || $[70] !== t28 || $[71] !== t29 || $[72] !== t30) {
    t31 = <div className={t21}><div className={t22}>{t27}{t28}{t29}{t30}</div></div>;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  return t31;
}
function _temp2(tab_0) {
  return {
    key: tab_0.focus,
    label: tab_0.title,
    value: tab_0.focus
  };
}
function _temp(w) {
  return w.wallet;
}